import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    model,
    signal
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { debounced } from 'src/app/pbir/utils/debounced';
import { Predicate } from 'src/app/pbir/utils/Predicate';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { SharedModule } from 'src/app/shared/shared.module';
import { isNullOrUndefined } from 'src/app/shared/utils';

@Component({
  selector: 'filter-editor__topn',
  template: `
    <div class="visual-editor-box__input">
      <label>Show</label>
      <div class="d-flex justify-content-between w-100p">
        <mat-select class="trg-outline user-select-none w-100p h-24"
                    panelClass="trg-outline-dropdown"
                    [(ngModel)]="predicate().operator"
                    (ngModelChange)="updatePredicate({ operator: $event })">
          <mat-option [value]="Operators.Top" title="Top">
            <span>Top</span>
          </mat-option>
          <mat-option [value]="Operators.Bottom" title="Bottom">
            <span>Bottom</span>
          </mat-option>
        </mat-select>
        <input type="number" min="1"
               class="trg-outline ml-4 max-w-80" 
               [value]="predicate().maxCount" 
               (change)="onMaxCountChange(maxCountInput)"
               (blur)="onMaxCountChange(maxCountInput)"
               #maxCountInput />
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelectModule, SharedModule],
  standalone: true
})
export class TopNFilterComponent {
  readonly Operators = Predicate.TopNOperator;

  readonly destroyRef = inject(DestroyRef);

  readonly predicate = model<Partial<VisualFilter.TopNPredicate>>({
    operator: Predicate.TopNOperator.Top,
    maxCount: 10
  });

  readonly prevMaxCount = signal(10);
  readonly updatePrevMaxCount = effect(() => this.prevMaxCount.set(this.predicate()?.maxCount ?? 1));

  updatePredicate(changes: Partial<VisualFilter.TopNPredicate>) {
    let predicate = {
      ...(this.predicate() ?? {}),
      ...changes
    };

    let maxCount = this.getSanitizedMaxCount(predicate.maxCount);

    this.predicate.set({
      type: predicate.type,
      target: predicate.target,
      operator: predicate.operator ?? Predicate.TopNOperator.Top,
      maxCount,
    });
  }

  onMaxCountChange(maxCountInput: HTMLInputElement) {
    let maxCount = this.getSanitizedMaxCount(maxCountInput.value);
    maxCountInput.value = maxCount.toFixed(0);
    this.updatePredicate({ maxCount });
  }

  private getSanitizedMaxCount(value: any) {
    let revertTo = this.prevMaxCount();

    if (isNullOrUndefined(value))
      return revertTo;

    if (typeof value !== 'number') 
      value = parseInt('' + value);
    
    if (isNaN(value) || value < 1)
      return revertTo;
    
    return Math.max(Math.floor(value), 1);
  }
}
